import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Store, User, Mail, Phone, Calendar, CreditCard } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { shopService, Shop } from '../../services/shopService';
import { customToast } from '../../utils/customToast';
import { format } from 'date-fns';

interface EditShopModalProps {
  isOpen: boolean;
  shop: Shop | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditShopModal: React.FC<EditShopModalProps> = ({ isOpen, shop, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    subscription_starts_at: '',
    subscription_ends_at: '',
    trial_ends_at: '',
    subscription_status: 'active' as 'active' | 'trial' | 'suspended' | 'expired',
    payment_status: 'paid' as 'paid' | 'pending' | 'overdue',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.shop_name || '',
        owner_name: shop.owner_name || '',
        owner_email: shop.owner_email || '',
        owner_phone: shop.owner_phone || '',
        subscription_starts_at: shop.subscription_starts_at ? format(new Date(shop.subscription_starts_at), 'yyyy-MM-dd') : '',
        subscription_ends_at: shop.subscription_ends_at ? format(new Date(shop.subscription_ends_at), 'yyyy-MM-dd') : '',
        trial_ends_at: shop.trial_ends_at ? format(new Date(shop.trial_ends_at), 'yyyy-MM-dd') : '',
        subscription_status: shop.subscription_status || 'active',
        payment_status: shop.payment_status || 'paid',
      });
    }
  }, [shop]);

  if (!isOpen && !isClosing || !shop) return null;

  const handleClose = () => {
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 150);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Shop name is required';
    }
    if (!formData.owner_name.trim()) {
      newErrors.owner_name = 'Owner name is required';
    }
    if (!formData.owner_email.trim()) {
      newErrors.owner_email = 'Owner email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      newErrors.owner_email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare data for submission
      const submitData: any = {
        shop_name: formData.name,  // Backend expects 'shop_name'
        owner_name: formData.owner_name,
        owner_email: formData.owner_email,
        owner_phone: formData.owner_phone,
        subscription_status: formData.subscription_status,
        payment_status: formData.payment_status,
      };

      // Add subscription fields if they are filled
      if (formData.subscription_starts_at) {
        submitData.subscription_starts_at = formData.subscription_starts_at;
      }
      if (formData.subscription_ends_at) {
        submitData.subscription_ends_at = formData.subscription_ends_at;
      }
      if (formData.trial_ends_at) {
        submitData.trial_ends_at = formData.trial_ends_at;
      }

      await shopService.updateShop(shop.id, submitData);
      customToast.success('Shop updated successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error updating shop:', error);
      customToast.error(error.response?.data?.message || 'Failed to update shop');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      style={{ touchAction: 'manipulation' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className={`w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-lg shadow-2xl my-auto ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 border-b flex-shrink-0 ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-green-900/50' : 'bg-green-100'
              }`}>
                <Store className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-green-400' : 'text-green-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  Edit Shop
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Update shop information
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-1.5 rounded-lg transition-colors touch-manipulation ${
                theme === 'dark'
                  ? 'hover:bg-slate-700 text-gray-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form id="edit-shop-form" onSubmit={handleSubmit}>
          <div className="px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
            <div className="space-y-3 sm:space-y-4">
              {/* Shop Information */}
              <div>
                <h3 className={`text-base font-medium flex items-center space-x-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <Store className="w-4 h-4" />
                  <span>Shop Information</span>
                </h3>
                <div className="space-y-3 mt-3">
                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Shop Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 rounded-md border text-xs sm:text-sm transition-colors touch-manipulation ${
                        errors.name
                          ? 'border-red-500 focus:ring-red-500'
                          : theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      } focus:outline-none focus:ring-2`}
                      placeholder="Enter shop name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400 flex items-center space-x-1">
                        <span>{errors.name}</span>
                      </p>
                    )}
                  </div>

                {/* Owner Information */}
                <div>
                  <h4 className={`text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner Information
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Owner Name *
                      </label>
                      <div className="relative">
                        <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="text"
                          value={formData.owner_name}
                          onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            errors.owner_name
                              ? 'border-red-500 focus:ring-red-500'
                              : theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                          placeholder="Enter owner name"
                        />
                      </div>
                      {errors.owner_name && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_name}</p>
                      )}
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Owner Email *
                      </label>
                      <div className="relative">
                        <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="email"
                          value={formData.owner_email}
                          onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            errors.owner_email
                              ? 'border-red-500 focus:ring-red-500'
                              : theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                          placeholder="owner@example.com"
                        />
                      </div>
                      {errors.owner_email && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.owner_email}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="tel"
                          value={formData.owner_phone}
                          onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>
                  </div>
                </div>

              {/* Subscription Information */}
              <div className="mt-4 sm:mt-5 md:mt-6">
                <h4 className={`text-xs sm:text-sm font-medium mb-3 sm:mb-4 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
                  Subscription Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subscription Start Date
                      </label>
                      <div className="relative">
                        <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="date"
                          value={formData.subscription_starts_at}
                          onChange={(e) => setFormData({ ...formData, subscription_starts_at: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subscription End Date
                      </label>
                      <div className="relative">
                        <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="date"
                          value={formData.subscription_ends_at}
                          onChange={(e) => setFormData({ ...formData, subscription_ends_at: e.target.value })}
                          min={formData.subscription_starts_at}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Trial End Date
                      </label>
                      <div className="relative">
                        <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="date"
                          value={formData.trial_ends_at}
                          onChange={(e) => setFormData({ ...formData, trial_ends_at: e.target.value })}
                          className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                          } focus:outline-none focus:ring-2`}
                        />
                      </div>
                      <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Set trial end date for trial subscriptions
                      </p>
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subscription Status
                      </label>
                      <select
                        value={formData.subscription_status}
                        onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value as any })}
                        className={`w-full px-3 sm:px-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                        } focus:outline-none focus:ring-2`}
                      >
                        <option value="active">Active</option>
                        <option value="trial">Trial</option>
                        <option value="suspended">Suspended</option>
                        <option value="expired">Expired</option>
                      </select>
                      <p className={`text-xs mt-2 ${
                        formData.subscription_status === 'suspended' 
                          ? 'text-red-600 font-medium' 
                          : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {formData.subscription_status === 'suspended' && '⚠️ Shop will not be able to login'}
                        {formData.subscription_status === 'expired' && '⚠️ Shop access is blocked'}
                        {formData.subscription_status === 'active' && '✓ Shop has full access'}
                        {formData.subscription_status === 'trial' && 'ℹ️ Shop is on trial period'}
                      </p>
                    </div>

                    <div className="sm:col-span-2">
                      <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Payment Status
                      </label>
                      <select
                        value={formData.payment_status}
                        onChange={(e) => setFormData({ ...formData, payment_status: e.target.value as any })}
                        className={`w-full px-3 sm:px-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                            : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                        } focus:outline-none focus:ring-2`}
                      >
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </form>

        {/* Footer */}
        <div className={`px-4 sm:px-6 py-4 border-t flex justify-center sm:justify-end ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={`w-full sm:w-auto px-6 py-3 sm:py-2.5 text-sm font-medium rounded-xl border transition-colors touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-shop-form"
              disabled={loading}
              className={`w-full sm:w-auto px-6 py-3 sm:py-2.5 text-sm font-medium rounded-xl transition-colors flex items-center justify-center space-x-2 touch-manipulation ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              <span>{loading ? 'Updating...' : 'Update Shop'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditShopModal;
